import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Languages, Zap, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * CEO-only widget shown in the Training Center header for MindEscapes.
 *
 * Shows cache coverage (ready / pending / missing) per language across every
 * active SOP and lets Dipali trigger a one-click pre-warm so the first-read
 * latency for Tamil / Hindi / Nepali / Kannada drops from 20-30s to zero.
 *
 * Backed by:
 *   GET  /api/me/training/translations/status
 *   POST /api/me/training/translations/prewarm   body: {lang?}
 */
export default function TranslationPrewarmWidget({ isCEO }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [lastPrewarm, setLastPrewarm] = useState(null);

  const load = async () => {
    try {
      const r = await api.get('/me/training/translations/status');
      setStatus(r.data);
    } catch (_) { /* non-CEO callers get 403 — silent. */ }
  };

  useEffect(() => { if (isCEO) load(); }, [isCEO]);

  // Auto-refresh while any language has pending translations.
  useEffect(() => {
    if (!isCEO || !status) return undefined;
    const hasPending = Object.values(status.per_lang || {}).some(l => l.pending > 0);
    if (!hasPending) return undefined;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [status, isCEO]);

  if (!isCEO || !status) return null;

  const runPrewarm = async (onlyLang) => {
    setBusy(true);
    try {
      const r = await api.post('/me/training/translations/prewarm', onlyLang ? { lang: onlyLang } : {});
      setLastPrewarm(r.data);
      await load();
    } finally { setBusy(false); }
  };

  const total = status.total_sops || 0;
  const per = status.per_lang || {};
  const langs = ['en', 'ta', 'hi', 'ne', 'kn'];
  const overall_ready_pct = Math.round(
    langs.reduce((a, l) => a + (per[l]?.coverage_pct || 0), 0) / Math.max(1, langs.length)
  );
  const anyPending = langs.some(l => (per[l]?.pending || 0) > 0);

  return (
    <div className="bg-white rounded-xl border p-3" style={{ borderColor: '#E5E1DC' }} data-testid="translation-prewarm-widget">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(v => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(v => !v); } }}
        className="w-full flex items-center justify-between gap-2 cursor-pointer"
        data-testid="translation-prewarm-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F3E8FF' }}>
            <Languages size={15} style={{ color: '#7C3AED' }} />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Translation Cache</p>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              {total} active SOPs · avg {overall_ready_pct}% warm across TA/HI/NE/KN
              {anyPending ? ' · translating…' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {anyPending && <RefreshCw size={13} className="animate-spin" style={{ color: '#7C3AED' }} />}
          <button
            type="button"
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); runPrewarm(null); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: '#7C3AED' }}
            data-testid="prewarm-all-btn"
          >
            <Zap size={11} /> {busy ? 'Queuing…' : 'Pre-warm all'}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2">
          {langs.map(l => {
            const v = per[l] || { label: l, ready: 0, pending: 0, failed: 0, stale: 0, missing: total, coverage_pct: 0 };
            return (
              <div key={l} className="flex items-center gap-2 text-[11px]" data-testid={`lang-row-${l}`}>
                <div className="w-28 font-medium" style={{ color: '#1B2A4A' }}>{v.label}</div>
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F3F4F6' }}>
                  <div className="h-full" style={{ width: `${v.coverage_pct}%`, backgroundColor: '#2D8B5E' }} />
                </div>
                <div className="w-14 text-right" style={{ color: '#1B2A4A' }}>{v.coverage_pct}%</div>
                <div className="w-28 text-[10px] text-right" style={{ color: '#6B7280' }}>
                  {v.ready} ready
                  {v.pending > 0 && <span style={{ color: '#7C3AED' }}> · {v.pending} queued</span>}
                  {v.failed > 0 && <span style={{ color: '#991B1B' }}> · {v.failed} failed</span>}
                  {v.stale > 0 && <span style={{ color: '#92400E' }}> · {v.stale} stale</span>}
                </div>
                <button
                  type="button"
                  disabled={busy || v.missing === 0 && v.failed === 0 && v.stale === 0}
                  onClick={() => runPrewarm(l)}
                  className="px-2 py-0.5 rounded text-[10px] font-semibold border disabled:opacity-40"
                  style={{ borderColor: '#E5E1DC', color: '#7C3AED' }}
                  data-testid={`prewarm-${l}-btn`}
                >
                  {v.missing + v.failed + v.stale > 0 ? `Queue ${v.missing + v.failed + v.stale}` : 'All warm'}
                </button>
              </div>
            );
          })}
          {lastPrewarm && (
            <div className="mt-2 text-[11px] flex items-start gap-1.5 p-2 rounded" style={{ backgroundColor: '#F0FDF4', color: '#166534' }} data-testid="prewarm-result">
              <CheckCircle2 size={12} className="mt-0.5 flex-shrink-0" />
              <span>Queued {lastPrewarm.queued} translations. {lastPrewarm.note}</span>
            </div>
          )}
          <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
            <AlertTriangle size={10} className="inline -mt-0.5" /> Translations run on gemini-2.5-flash in the background, ~2 in parallel at a time. Cached translations serve instantly.
          </p>
        </div>
      )}
    </div>
  );
}
